import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Rocket, Users, TrendingUp, Flame, Snowflake, Sparkles, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const STATUS_COLORS = {
  new: "bg-secondary text-foreground",
  contacted: "bg-blue-500/15 text-blue-500",
  qualified: "bg-primary/15 text-primary",
  meeting: "bg-purple-500/15 text-purple-500",
  won: "bg-emerald-500/15 text-emerald-500",
  lost: "bg-destructive/15 text-destructive",
};

const TEMP_ICONS = {
  hot: { icon: Flame, className: "text-orange-500" },
  warm: { icon: TrendingUp, className: "text-amber-500" },
  cold: { icon: Snowflake, className: "text-sky-500" },
};

function Stat({ label, value, hint }) {
  return (
    <div data-testid={`growth-stat-${label.toLowerCase().replace(/\s+/g, '-')}`} className="rounded-xl border border-border bg-card p-5">
      <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{label}</div>
      <div className="font-display text-3xl mt-2">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

export default function GrowthOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/growth/analytics");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="p-6 md:p-8 space-y-8" data-testid="growth-overview">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
            <Rocket className="h-3.5 w-3.5" /> Growth Engine
          </div>
          <h1 className="font-display text-4xl mt-2">B2B Lead Intelligence.</h1>
          <p className="text-muted-foreground mt-1 text-sm max-w-lg">
            Discover, enrich, qualify and reach out to the businesses that matter — powered by Claude Sonnet 4.5.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/app/growth/leads"><Button variant="outline" className="rounded-full" data-testid="growth-cta-leads">View all leads</Button></Link>
          <Link to="/app/growth/chat"><Button className="rounded-full gap-2" data-testid="growth-cta-chat"><Sparkles className="h-4 w-4" />Ask Prospect AI</Button></Link>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading analytics…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Total Leads" value={data?.total_leads ?? 0} />
            <Stat label="Qualified" value={data?.qualified ?? 0} hint="qualified + meeting + won" />
            <Stat label="Conversion Rate" value={`${data?.conversion_rate ?? 0}%`} hint="won / total" />
            <Stat label="Hot Leads" value={data?.by_temperature?.hot ?? 0} hint="score ≥ 75" />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-3">Pipeline · By Status</div>
              <div className="space-y-2">
                {["new", "contacted", "qualified", "meeting", "won", "lost"].map((k) => (
                  <div key={k} className="flex items-center gap-3 text-sm">
                    <span className={"text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full " + (STATUS_COLORS[k] || "")}>{k}</span>
                    <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${Math.min(100, ((data?.by_status?.[k] || 0) / Math.max(1, data?.total_leads || 1)) * 100)}%` }} />
                    </div>
                    <span className="tabular-nums text-muted-foreground text-xs w-8 text-right">{data?.by_status?.[k] || 0}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-border bg-card p-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-3">Top Industries</div>
              {(data?.top_industries?.length ? data.top_industries : []).map((row) => (
                <div key={row.name} className="flex items-center justify-between py-1.5 text-sm border-b border-border last:border-0">
                  <span>{row.name}</span>
                  <span className="text-muted-foreground tabular-nums">{row.count}</span>
                </div>
              ))}
              {(!data?.top_industries || data.top_industries.length === 0) && (
                <div className="text-xs text-muted-foreground italic">No leads yet. Add or import to see analytics.</div>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {Object.entries(TEMP_ICONS).map(([k, v]) => {
              const Icon = v.icon;
              return (
                <div key={k} className="rounded-xl border border-border bg-card p-5">
                  <div className="flex items-center gap-2">
                    <Icon className={"h-5 w-5 " + v.className} />
                    <div className="font-display text-lg capitalize">{k} Leads</div>
                  </div>
                  <div className="font-display text-3xl mt-3">{data?.by_temperature?.[k] || 0}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {k === "hot" ? "Score ≥ 75 · reach out today" : k === "warm" ? "Score 50-74 · nurture" : "Score < 50 · re-engage later"}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="rounded-xl border border-primary/30 bg-primary/5 p-5 flex items-center justify-between">
            <div>
              <div className="font-display text-lg">Ready to fill the pipeline?</div>
              <div className="text-sm text-muted-foreground mt-1">Import leads from a CSV, add manually, or ask Prospect AI to help.</div>
            </div>
            <Link to="/app/growth/leads">
              <Button className="rounded-full gap-2" data-testid="growth-cta-fill"><ArrowRight className="h-4 w-4" /> Manage leads</Button>
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
